import {
  OLLIE_FINDING_ACTION_MODES,
  pickOllieFindingCardAction,
} from './ollieFindingCards'

/**
 * Phase 4 — slim "Ollie noticed" rows on the screens where the problem is.
 * Pure, so the rows only render. Every figure is the finding's own copy.
 */

export const OLLIE_NOTICED_PAGES = Object.freeze({
  ORDERS: 'orders',
  RECEIPTING: 'receipting',
  INVOICES: 'invoices',
  FOOD_SAFETY: 'food_safety',
  MENU_BUILDER: 'menu_builder',
  STOCK: 'stock',
  SUCCESS_HUB: 'success_hub',
})

/** Any act / setup check (Success Hub: Ollie's home, not one feature). */
export const OLLIE_NOTICED_ANY_CHECK = '*'

/**
 * Page → the checks that belong there, in the order they're worth reading.
 * A check can sit on more than one page; Express decides who sees it.
 */
export const OLLIE_NOTICED_PAGE_CHECKS = Object.freeze({
  [OLLIE_NOTICED_PAGES.ORDERS]: Object.freeze([
    'deliveries_not_checked_in',
    'price_rises',
  ]),
  [OLLIE_NOTICED_PAGES.RECEIPTING]: Object.freeze([
    'deliveries_not_checked_in',
    'checked_in_not_reconciled',
    'credits_still_owed',
  ]),
  [OLLIE_NOTICED_PAGES.INVOICES]: Object.freeze([
    'missing_invoices',
    'checked_in_not_reconciled',
  ]),
  [OLLIE_NOTICED_PAGES.FOOD_SAFETY]: Object.freeze([
    'food_safety_out_of_range',
    'food_safety_overdue',
  ]),
  [OLLIE_NOTICED_PAGES.MENU_BUILDER]: Object.freeze([
    'recipes_gp_drift',
    'dishes_without_gp',
  ]),
  [OLLIE_NOTICED_PAGES.STOCK]: Object.freeze([
    'stock_take_overdue',
    'stock_count_unexplained',
    'stock_usage_jump',
    'stock_take_first',
  ]),
  [OLLIE_NOTICED_PAGES.SUCCESS_HUB]: Object.freeze([OLLIE_NOTICED_ANY_CHECK]),
})

/** One at a time: the next finding takes its place once this one is done or dismissed. */
export const OLLIE_NOTICED_MAX_ROWS = 1

/** Pages that allow fewer rows than the default. */
export const OLLIE_NOTICED_PAGE_MAX_ROWS = Object.freeze({})

export const ollieNoticedMaxRowsFor = page =>
  OLLIE_NOTICED_PAGE_MAX_ROWS[page] ?? OLLIE_NOTICED_MAX_ROWS

/**
 * Checks whose title alone doesn't say where to start, so the row carries
 * the finding's noticed line under it, like the panel card ("Start with
 * Walk-in fridge: …").
 */
export const OLLIE_NOTICED_DETAIL_CHECKS = Object.freeze([
  'food_safety_out_of_range',
  'food_safety_overdue',
])

export const ollieNoticedTakesAnyCheck = page =>
  ollieNoticedChecksFor(page).includes(OLLIE_NOTICED_ANY_CHECK)

/** Identity of the shown rows (check + fingerprint), for change-only callbacks. */
export const ollieNoticedRowsKey = (rows = []) =>
  (rows || [])
    .map(row => `${row?.checkId || ''}:${row?.finding?.fingerprint || ''}`)
    .join('|')

export const OLLIE_NOTICED_COPY = Object.freeze({
  label: 'Ollie noticed',
  why: 'Why?',
  dismiss: 'Dismiss',
})

const UK_TIME_ZONE = 'Europe/London'
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export function ollieNoticedChecksFor(page) {
  return OLLIE_NOTICED_PAGE_CHECKS[page] || []
}

const ukDateKey = date =>
  new Intl.DateTimeFormat('en-CA', { timeZone: UK_TIME_ZONE }).format(date)

/** Same as Express `formatOllieAsOf`: "9:05" today, "29 Sep, 21:30" before. */
export function formatOllieAsOfLabel(asOf, now = new Date()) {
  const date = asOf ? new Date(asOf) : null
  if (!date || Number.isNaN(date.getTime())) return null
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
  const dateKey = ukDateKey(date)
  if (dateKey === ukDateKey(now)) return time
  const [, month, day] = dateKey.split('-').map(Number)
  return `${day} ${MONTHS[month - 1]}, ${time}`
}

/**
 * @param {{ findings: object[], page: string, venueId: string,
 *   asOf?: string|null, currentPath?: string, now?: Date }} params
 *   `findings` from `listOllieFindingsForChecks` (Express rank order).
 *   `currentPath` is pathname + search: an action that would reopen the
 *   screen they're already on is left off. `excludeCheckIds` drops findings
 *   already on screen elsewhere (Success Hub: today's Ollie's pick).
 */
export function buildOllieNoticedRows({
  findings = [],
  page,
  venueId,
  asOf = null,
  currentPath = '',
  excludeCheckIds = [],
  now = new Date(),
}) {
  const wanted = ollieNoticedChecksFor(page)
  if (!wanted.length || !venueId) return []
  const anyCheck = wanted.includes(OLLIE_NOTICED_ANY_CHECK)
  const excluded = new Set(excludeCheckIds || [])
  const asOfTime = formatOllieAsOfLabel(asOf, now)
  return (findings || [])
    .filter(
      finding =>
        finding?.checkId &&
        finding.kind !== 'notice' &&
        finding.copy?.title &&
        !excluded.has(finding.checkId) &&
        (anyCheck || wanted.includes(finding.checkId)),
    )
    .slice(0, ollieNoticedMaxRowsFor(page))
    .map(finding => {
      const action = pickOllieFindingCardAction(finding, venueId)
      const navigates = action?.mode === OLLIE_FINDING_ACTION_MODES.NAVIGATE
      return {
        checkId: finding.checkId,
        title: finding.copy.title,
        noticed: OLLIE_NOTICED_DETAIL_CHECKS.includes(finding.checkId)
          ? String(finding.copy.noticed || '').trim() || null
          : null,
        asOfLabel: asOfTime ? `as of ${asOfTime}` : null,
        action:
          action && !(navigates && action.path === currentPath) ? action : null,
        finding,
      }
    })
}
